import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { Download } from 'lucide-react';
import { FileText } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { Phone } from 'lucide-react';
import { Mail } from 'lucide-react';
import { MapPin } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Clock } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { Building2 } from 'lucide-react';
import { Info } from 'lucide-react';

// Verified data from the Provider/Vendor Procurement Packet (Aug 2026)
const COMPANY = {
  legalName: 'MoveraCare Services LLC',
  dba: 'MoveraCare Mobility',
  address: '19030 Grand Vista Springs Blvd, Richmond, TX 77407',
  npi: '1316651292',
  naics: '485991 — Special Needs Transportation',
  providerType: 'Non-Emergency Medical Transportation (NEMT) — Wheelchair & Ambulatory',
  businessType: 'EMT-Owned & Operated NEMT',
  serviceArea: 'Greater Houston, Texas — Harris, Fort Bend, Brazoria, Montgomery, and Galveston Counties',
  website: 'www.moveracaremobility.com',
  mainPhone: '832-925-2128',
  tollFree: '832-925-2128',
  fax: '713-583-2334',
  referralIntake: 'moveracareservices@gmail.com',
  contractingEmail: 'dispatch@moveracaremobility.com',
  operatingHours: 'Mon–Sat 6:00 AM – 7:00 PM · Sunday 12:00 PM – 5:00 PM',
  hcpcs: 'A0130, S0209, T2003, S0215, T2007',
};

const DOCUMENTS = [
  { name: 'Provider / Vendor Procurement Packet', desc: 'Complete public packet — company profile, capabilities, service area, workflow, billing.', status: 'available', href: '/MoveraCare-Vendor-Procurement-Packet.pdf', download: true },
  { name: 'Company Capability Statement', desc: 'One-page summary of services, coverage, and contracting fit.', status: 'request' },
  { name: 'W-9', desc: 'Current W-9 for authorized contracting parties.', status: 'request' },
  { name: 'Certificate of Insurance (COI)', desc: 'Current COI available to authorized contracting entities.', status: 'request' },
  { name: 'NPI Confirmation', desc: 'National Provider Identifier documentation.', status: 'request' },
  { name: 'Service Area & Coverage Sheet', desc: 'Verified counties, cities, and extended-distance policy.', status: 'request' },
  { name: 'Fleet & Vehicle Information', desc: 'Wheelchair-accessible vehicle records and capabilities.', status: 'request' },
  { name: 'Driver Qualification Standards', desc: 'Screening, training, and documentation standards.', status: 'request' },
  { name: 'Safety & Compliance Overview', desc: 'Vehicle inspection, incident reporting, privacy, and safety policies.', status: 'request' },
  { name: 'HIPAA / Privacy Information', desc: 'Passenger and claim information handling policies.', status: 'request' },
  { name: 'Provider Rate / Fee Schedule', desc: 'Contract-specific. Standard fee schedule available to authorized contracting partners.', status: 'request' },
  { name: 'Additional Credentialing Documents', desc: 'Business registration, licensing/permit records, sample invoice, compliance attestations.', status: 'request' },
];

const CREDENTIALING_CHECKLIST = [
  { item: 'Company Profile', status: 'Available' },
  { item: 'Vendor Procurement Packet', status: 'Available' },
  { item: 'W-9', status: 'Available Upon Request' },
  { item: 'Certificate of Insurance', status: 'Available Upon Request' },
  { item: 'NPI Documentation', status: 'Available Upon Request' },
  { item: 'Service Area & Coverage', status: 'Available' },
  { item: 'Capability Statement', status: 'Available Upon Request' },
  { item: 'Fleet Information', status: 'Available Upon Request' },
  { item: 'Driver Standards', status: 'Available Upon Request' },
  { item: 'Safety & Compliance Policies', status: 'Available Upon Request' },
  { item: 'Provider Rate / Fee Schedule', status: 'Available Upon Request' },
  { item: 'Contact Sheet', status: 'Available' },
];

const WORKFLOW_STEPS = [
  'Transportation Request',
  'Authorization / Confirmation',
  'Scheduling',
  'Dispatch',
  'Pickup',
  'Transportation',
  'Appointment / Destination',
  'Return Trip (if authorized)',
  'Trip Completion',
  'Documentation',
  'Billing',
];

const Procurement = () => {
  const [formStatus, setFormStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus('');
    const fd = new FormData(e.target);
    const payload = {
      organization: fd.get('organization'),
      org_type: fd.get('orgType'),
      contact_name: fd.get('contactName'),
      title: fd.get('title') || null,
      email: fd.get('email'),
      phone: fd.get('phone') || null,
      department: fd.get('department') || null,
      interest: fd.get('interest') || null,
      volume: fd.get('volume') || null,
      transport_type: fd.get('transportType') || null,
      message: fd.get('message') || null,
    };
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries/vendor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('submission failed');
      setFormStatus('success');
      e.target.reset();
    } catch (err) {
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="procurement-page">
      <Seo
        title="NEMT Provider Procurement & Credentialing Houston | MoveraCare Mobility"
        description="Non-emergency medical transportation provider procurement, credentialing, and vendor documentation resources for hospitals, TPAs, insurance carriers, and NEMT networks. Download the MoveraCare Vendor Procurement Packet."
        path="/procurement"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Procurement', url: '/procurement' },
        ]}
      />
      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <Building2 className="w-4 h-4" />
              <span>PROCUREMENT · CREDENTIALING · VENDOR SETUP</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Provider &amp; Vendor Procurement Resources
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Everything procurement, credentialing, network-management, and healthcare partners need to evaluate MoveraCare Mobility as a non-emergency medical transportation provider.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/MoveraCare-Vendor-Procurement-Packet.pdf" target="_blank" rel="noopener noreferrer" download>
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="cta-download-packet">
                  <Download className="mr-2 w-5 h-5" />
                  Download Complete Vendor Packet
                </Button>
              </a>
              <a href={`mailto:${COMPANY.contractingEmail}?subject=Provider%20Relations%20Inquiry`}>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-contact-provider-relations">
                  <Mail className="mr-2 w-5 h-5" />
                  Contact Provider Relations
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Company Profile */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-2">Company Profile</h2>
          <p className="text-gray-600 mb-8">Verified business information for vendor review and credentialing.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-2 border-gray-100">
              <CardContent className="p-6 space-y-3">
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Legal Entity</p><p className="text-gray-800 font-semibold">{COMPANY.legalName}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Doing Business As</p><p className="text-gray-800 font-semibold">{COMPANY.dba}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Provider Type</p><p className="text-gray-800">{COMPANY.providerType}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Business Type</p><p className="text-gray-800">{COMPANY.businessType}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Business Address</p><p className="text-gray-800">{COMPANY.address}</p></div>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100">
              <CardContent className="p-6 space-y-3">
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">NPI</p><p className="text-gray-800 font-mono font-semibold" data-testid="company-npi">{COMPANY.npi}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">NAICS</p><p className="text-gray-800">{COMPANY.naics}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">Primary Service Area</p><p className="text-gray-800">{COMPANY.serviceArea}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-[#F9A825] font-bold mb-1">HCPCS Reference</p><p className="text-gray-800 font-mono text-sm">{COMPANY.hcpcs}</p><p className="text-xs text-gray-500 mt-1 italic">Final code selection, modifiers, reimbursement, and authorization are payer- and contract-specific.</p></div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Document Center */}
      <section className="py-16 bg-gray-50" data-testid="document-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-2">Procurement Document Center</h2>
            <p className="text-gray-600">Sensitive credentialing records are separated from public materials to protect business, driver, insurance, tax, and banking information.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOCUMENTS.map((doc) => (
              <Card key={doc.name} className="border-2 border-gray-100 hover:border-[#F9A825] transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[#004C97] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#F9A825]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#004C97] text-sm leading-tight mb-1">{doc.name}</h3>
                      <p className="text-xs text-gray-600 leading-relaxed">{doc.desc}</p>
                    </div>
                  </div>
                  {doc.status === 'available' && doc.href ? (
                    <a href={doc.href} target="_blank" rel="noopener noreferrer" download={doc.download}>
                      <Button size="sm" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-semibold w-full">
                        <Download className="mr-2 w-4 h-4" />
                        Download PDF
                      </Button>
                    </a>
                  ) : (
                    <a href={`mailto:${COMPANY.contractingEmail}?subject=Document%20Request%3A%20${encodeURIComponent(doc.name)}`}>
                      <Button size="sm" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold w-full">
                        <Mail className="mr-2 w-4 h-4" />
                        Available Upon Request
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentialing Checklist */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-2">Credentialing Document Checklist</h2>
          <p className="text-gray-600 mb-8">A quick reference for credentialing and vendor-setup teams.</p>
          <Card className="border-2 border-gray-100">
            <CardContent className="p-0">
              <ul className="divide-y divide-gray-100">
                {CREDENTIALING_CHECKLIST.map((c) => (
                  <li key={c.item} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
                    <span className="text-gray-800 font-medium">{c.item}</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${c.status === 'Available' ? 'bg-green-100 text-green-800' : 'bg-[#F9A825]/20 text-[#004C97]'}`}>
                      {c.status.toUpperCase()}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Transportation Coordination Workflow */}
      <section className="py-16 bg-gray-50" data-testid="workflow-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-2 text-center">Transportation Coordination Workflow</h2>
          <p className="text-gray-600 text-center mb-10">A practical referral-to-billing workflow for coordinators, adjusters, case managers, TPAs, hospitals, and healthcare facilities.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {WORKFLOW_STEPS.map((step, idx) => (
              <div key={step} className="bg-white rounded-lg border-2 border-gray-100 p-4 flex items-center gap-3 hover:border-[#F9A825] transition-colors">
                <div className="w-8 h-8 rounded-full bg-[#004C97] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{idx + 1}</div>
                <span className="text-gray-800 font-semibold text-sm">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance / Compliance Notice */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#F9A825]/10 border-l-4 border-[#F9A825] rounded-lg p-6 md:p-8">
            <div className="flex items-start gap-4">
              <ShieldCheck className="w-8 h-8 text-[#F9A825] flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-[#004C97] mb-3">Sensitive Documents — Protected Access</h3>
                <p className="text-gray-700 leading-relaxed">
                  W-9s, full Certificates of Insurance, banking forms, driver credentials, signatures, and other sensitive records are <strong>not published for unrestricted public download</strong>. Authorized contracting organizations may request the complete credentialing set directly through Provider Relations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Provider Relations Contact */}
      <section className="py-16 bg-[#004C97]" data-testid="provider-relations-contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2 text-center">Provider Relations Contacts</h2>
          <p className="text-gray-200 text-center mb-10">Direct contacts for procurement, credentialing, contracting, and dispatch.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-white/10 border border-[#F9A825]/30 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-[#F9A825] mb-4">Contracting, Provider Relations &amp; Billing</h3>
                <div className="space-y-3 text-gray-100">
                  <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#F9A825]" /><a href={`mailto:${COMPANY.contractingEmail}`} className="hover:text-[#F9A825] transition-colors">{COMPANY.contractingEmail}</a></div>
                  <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#F9A825]" /><a href={"tel:+18329252128"} className="hover:text-[#F9A825] transition-colors font-semibold">Main: {COMPANY.mainPhone}</a></div>
                  <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#F9A825]" /><a href={"tel:+18329252128"} className="hover:text-[#F9A825] transition-colors">Toll-Free: {COMPANY.tollFree}</a></div>
                  <div className="flex items-center gap-3"><Info className="w-4 h-4 text-[#F9A825]" /><span>Fax: {COMPANY.fax}</span></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border border-[#F9A825]/30 backdrop-blur-sm">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-[#F9A825] mb-4">Referral Intake (After Vendor Setup)</h3>
                <div className="space-y-3 text-gray-100">
                  <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#F9A825]" /><a href={`mailto:${COMPANY.referralIntake}`} className="hover:text-[#F9A825] transition-colors break-all">{COMPANY.referralIntake}</a></div>
                  <div className="flex items-start gap-3"><Clock className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" /><span className="text-sm">{COMPANY.operatingHours}</span></div>
                  <div className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#F9A825] mt-1 flex-shrink-0" /><span className="text-sm">{COMPANY.address}</span></div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Vendor Inquiry Form */}
      <section className="py-16 bg-gray-50" data-testid="vendor-inquiry-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-3">Vendor / Provider Enrollment Inquiry</h2>
            <p className="text-gray-600">For procurement, credentialing, and provider-relations teams.</p>
          </div>
          <Card className="border-2 border-gray-100">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="vendor-inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Organization Name *</label><input required name="organization" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Organization Type *</label><input required name="orgType" placeholder="Hospital, TPA, MCO, NEMT broker…" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Contact Name *</label><input required name="contactName" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Job Title</label><input name="title" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Business Email *</label><input required type="email" name="email" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Phone</label><input name="phone" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Department</label>
                    <select name="department" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none">
                      <option value="Procurement">Procurement</option>
                      <option value="Credentialing">Credentialing</option>
                      <option value="Provider Relations">Provider Relations</option>
                      <option value="Network Management">Network Management</option>
                      <option value="Claims">Claims</option>
                      <option value="Case Management">Case Management</option>
                      <option value="Transportation Coordination">Transportation Coordination</option>
                      <option value="Hospital Administration">Hospital Administration</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Interest</label>
                    <select name="interest" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none">
                      <option>Provider Enrollment</option>
                      <option>Transportation Contract</option>
                      <option>Credentialing</option>
                      <option>Rate Request</option>
                      <option>Workers&apos; Compensation</option>
                      <option>NEMT Network</option>
                      <option>Hospital Partnership</option>
                      <option>Dialysis Partnership</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Est. Monthly Trip Volume</label><input name="volume" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none" /></div>
                  <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Transportation Type</label>
                    <select name="transportType" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none">
                      <option>Wheelchair</option><option>Ambulatory</option><option>Both</option>
                    </select>
                  </div>
                </div>
                <div><label className="block text-sm font-semibold text-[#004C97] mb-1">Message</label><textarea name="message" rows="4" className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-[#F9A825] focus:outline-none"></textarea></div>
                <Button type="submit" disabled={submitting} size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold w-full" data-testid="submit-vendor-inquiry">
                  {submitting ? 'Submitting…' : 'Submit Vendor Inquiry'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800 text-sm flex items-start gap-2" data-testid="form-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                    <span>Thank you. Your inquiry has been received and routed to Provider Relations at {COMPANY.contractingEmail}. You&apos;ll hear back within 1–2 business days.</span>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm flex items-start gap-2" data-testid="form-error">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>Sorry — submission failed. Please email us directly at {COMPANY.contractingEmail} or call {COMPANY.mainPhone}.</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 text-center pt-2">
                  Submissions are secured and reviewed by Provider Relations. This form does not confirm enrollment or contracting.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Non-Emergency Disclaimer */}
      <section className="py-8 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong>. We do not market our services as ambulance, emergency response, 911 transport, or stretcher ambulance care. Emergency conditions should be handled through appropriate emergency medical services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Procurement;
